import { jsx as _jsx } from "react/jsx-runtime";
import config from '@iotv/config';
import { Device, DeviceConfig, getDeviceContextFromNodeEdgeMapper, isDeviceConfigLike, isDeviceLike } from '@iotv/datamodel-core';
import { DeviceTransactionStatus, getZombieInstanceFromPrimaryKey, isValidBusinessObject } from '@iotv/iotv-v3-types';
import { getDeviceEnabledTypesIds } from '@iotv/payload-definition';
import { useContext, useEffect, useReducer, useRef } from 'react';
import { v4 } from 'uuid';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { subscribeToMQTTChannel } from '../../data/daoFunctions/daoFunctions';
import { Severity } from '../../types/AppTypes';
import { EntityControlReducer, SingleEntityControlContext, defaultState, isValidDCT, } from '../../v2/components/generic/Control/Entity/Reducers/EnitityControlReducer';
import { DeviceConfigContext, DeviceContextReducer, defaultState as defaultDeviceContextState } from './Context/deviceConfigContext';
import DeviceForm from './DeviceForm';
const debug = process.env.REACT_APP_DEBUG && false;
const DeviceViewBase = (props) => {
    debug && console.log(`DeviceViewBase rendering with`, props);
    // additional tabs passed though to DetailForm
    const { viewObject, userFunctions: UserFunctions, user: contextUser } = props;
    const { matchedPrimary } = viewObject;
    const [state, dispatch] = useReducer(DeviceContextReducer, defaultDeviceContextState);
    const { device, downlinkHistory } = state;
    const { state: { FifoDCTs }, } = useContext(SingleEntityControlContext);
    useEffect(() => {
        if (!FifoDCTs || FifoDCTs.length === 0)
            return;
        debug && console.log(`DeviceViewBase FiFoDCTs changed`);
        const dcts = { ...structuredClone(downlinkHistory), ...Object.fromEntries(structuredClone(FifoDCTs).map((dct) => [dct.sk, dct])) };
        debug && console.log(`DeviceViewBase dispatching dcts from SingleEntityControlContext`, dcts);
        dispatch({ downlinkHistory: dcts });
    }, [FifoDCTs]);
    const [entityControlState, entityControlDispatch] = useReducer(EntityControlReducer, defaultState);
    const dctChannelSubRef = useRef(undefined);
    const responseChannelSubRef = useRef(undefined);
    const deviceConfigSubRef = useRef(undefined);
    useEffect(() => {
        if (!matchedPrimary || !isDeviceLike(matchedPrimary) || !contextUser || !device)
            return;
        const dctTopic = config.aws.mqtt.deviceControlTransactionChannel.replace('[deviceSk]', device.sk);
        dctChannelSubRef.current = subscribeToMQTTChannel(contextUser, dctTopic, (data) => {
            const receivedObject = data.value;
            if (isValidDCT(receivedObject)) {
                const { status } = receivedObject;
                debug && console.log(`Got ${receivedObject.status} on DCT channel`, receivedObject);
                if ([DeviceTransactionStatus.REJECTED_DUPLICATE_DOWNLINK].includes(status)) {
                    UserFunctions.setUserMessage({
                        id: v4(),
                        type: 'MessageBar',
                        severity: Severity.warning,
                        label: 'Duplicate',
                        content: `Downlink rejected as duplicate`,
                    });
                }
                else {
                    const action = { type: 'AddTransactions', value: { entity: matchedPrimary, dcts: [receivedObject] } };
                    entityControlDispatch(action);
                }
            }
        });
        const responseTopic = config.aws.mqtt.deviceControlResponseChannel.replace('[deviceSk]', device.sk);
        responseChannelSubRef.current = subscribeToMQTTChannel(contextUser, responseTopic, (data) => {
            const receivedObject = data.value;
            debug && console.log('Got message on response channel', receivedObject);
        });
        return () => {
            dctChannelSubRef.current?.unsubscribe();
            responseChannelSubRef.current?.unsubscribe();
        };
    }, [contextUser?.sk, device?.sk]);
    useEffect(() => {
        if (contextUser) {
            const deviceConfigTopic = config.aws.mqtt.deviceConfigUpdateChannel.replace('[deviceSk]', '#');
            deviceConfigSubRef.current = subscribeToMQTTChannel(contextUser, deviceConfigTopic, (data) => {
                const deviceConfig = data.value;
                debug && console.log('Got message on device config channel', deviceConfig);
                if (isDeviceConfigLike(deviceConfig)) {
                    debug && console.log('Updating NEM with deviceContext', deviceConfig);
                    dispatch({ deviceConfig: new DeviceConfig(deviceConfig) });
                }
            });
        }
        return () => deviceConfigSubRef.current?.unsubscribe();
    }, [contextUser?.sk]);
    const getDeviceContext = async (device) => {
        const { err, data } = await ConsumerDao.getDeviceContext({ device });
        if (err)
            console.log(`ERR: getDeviceContext`, err.message);
        if (data) {
            debug && console.log(data.nodeEdgeMapper.getSummary());
            const deviceEnabledTypeIds = getDeviceEnabledTypesIds(device);
            const nodeEdgeMapper = data.nodeEdgeMapper;
            const { entity, deviceConfig, deviceHistory } = getDeviceContextFromNodeEdgeMapper({ nodeEdgeMapper, device, deviceEnabledTypeIds });
            if (!deviceConfig || !isDeviceConfigLike(deviceConfig)) {
                console.error(`getDeviceContext: deviceConfig is not valid`, { deviceConfig });
            }
            dispatch({ deviceEnabledTypeIds, nodeEdgeMapper, entity, deviceConfig, deviceHistory });
            if (deviceConfig?.lastRequestedBroadcastScheduleSk) {
                const zombie = getZombieInstanceFromPrimaryKey(deviceConfig.lastRequestedBroadcastScheduleSk);
                const { err, data: broadcastScheduleObjectData } = await ConsumerDao.getExact(zombie);
                if (err)
                    console.error('getDeviceConfig get broadcastScheduleObject', err.message);
                if (broadcastScheduleObjectData) {
                    const broadcastScheduleObject = broadcastScheduleObjectData.Items?.at(0) ?? null;
                    dispatch({ broadcastScheduleObject });
                }
            }
        }
    };
    const getLNS = async (device) => {
        if (device.lns) {
            debug && console.log('retreiving device lns');
            const { err, data } = await ConsumerDao.getExact(getZombieInstanceFromPrimaryKey(device.lns));
            if (err)
                console.error('ERR: getLNS', err.message);
            const item = data?.Items?.at(0);
            if (isValidBusinessObject(item)) {
                dispatch({ lns: item });
            }
        }
    };
    useEffect(() => {
        if (!matchedPrimary || !isDeviceLike(matchedPrimary))
            return;
        const device = new Device(matchedPrimary);
        dispatch({ device });
        getDeviceContext(device);
        getLNS(device);
        entityControlDispatch({ type: 'LoadEntity', value: { Entity: device } });
    }, [matchedPrimary?.sk]);
    return (_jsx(DeviceConfigContext.Provider, { value: { state, dispatch }, children: _jsx(SingleEntityControlContext.Provider, { value: { state: entityControlState, dispatch: entityControlDispatch }, children: _jsx(DeviceForm, { ...props }) }) }));
};
export default DeviceViewBase;
